import React, { useEffect, useState } from "react";
import PageShell from "@/components/PageShell";
import { FadeUp } from "@/components/Reveal";
import {
  authLogin,
  authMe,
  authLogout,
  authHasToken,
  listSubmissions,
  deleteSubmission,
  listNdaRequests,
  deleteNdaRequest,
  revokeNdaRequest,
  unrevokeNdaRequest,
  pressStats,
} from "@/lib/api";
import { toast } from "sonner";
import { Trash2, RefreshCw, FileDown, LogOut, Ban, RotateCcw } from "lucide-react";
import AdminLibrary from "@/components/AdminLibrary";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function formatErr(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Admin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("inbox");
  const [inbox, setInbox] = useState([]);
  const [ndas, setNdas] = useState([]);
  const [press, setPress] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (authHasToken()) {
      restoreSession();
    }
  }, []);

  const restoreSession = async () => {
    try {
      const u = await authMe();
      setMe(u);
      await Promise.all([refreshInbox(), refreshNdas(), refreshPress()]);
    } catch {
      // token invalid / expired — silently clear
      await authLogout();
      setMe(null);
    }
  };

  const login = async (e) => {
    e.preventDefault();
    if (!email || !password) return toast.error("Email and password are required.");
    try {
      setLoading(true);
      const res = await authLogin(email, password);
      setMe(res.admin);
      setPassword("");
      await Promise.all([refreshInbox(), refreshNdas(), refreshPress()]);
      toast.success("Signed in.");
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    await authLogout();
    setMe(null);
    setInbox([]);
    setNdas([]);
    toast.success("Signed out.");
  };

  const refreshInbox = async () => {
    try {
      setInbox(await listSubmissions());
    } catch {
      toast.error("Could not fetch submissions.");
    }
  };
  const refreshNdas = async () => {
    try {
      setNdas(await listNdaRequests());
    } catch {
      toast.error("Could not fetch NDA requests.");
    }
  };
  const refreshPress = async () => {
    try {
      setPress(await pressStats());
    } catch {
      // silent
    }
  };

  const removeInbox = async (id) => {
    if (!window.confirm("Delete this submission?")) return;
    try {
      await deleteSubmission(id);
      setInbox((xs) => xs.filter((x) => x.id !== id));
      toast.success("Deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };
  const removeNda = async (id) => {
    if (!window.confirm("Delete this NDA request?")) return;
    try {
      await deleteNdaRequest(id);
      setNdas((xs) => xs.filter((x) => x.id !== id));
      toast.success("Deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };
  const toggleRevoke = async (n) => {
    try {
      const updated = n.revoked ? await unrevokeNdaRequest(n.id) : await revokeNdaRequest(n.id);
      setNdas((xs) => xs.map((x) => (x.id === n.id ? updated : x)));
      toast.success(updated.revoked ? "Link revoked." : "Link re-enabled.");
    } catch {
      toast.error("Action failed.");
    }
  };

  const daysLeft = (iso) => {
    const ms = new Date(iso).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / 86400000));
  };

  return (
    <PageShell title="Admin — NewGenPharma">
      <section className="px-6 md:px-10 pt-16 md:pt-24 pb-32 min-h-[70vh]">
        <div className="mx-auto max-w-[1600px]">
          <FadeUp>
            <div className="font-mono-tight text-[11px] text-ink/60 flex items-center gap-4 mb-8">
              <span className="w-1.5 h-1.5 bg-oxred inline-block" />
              Admin · Console
            </div>
          </FadeUp>

          {!me ? (
            <FadeUp>
              <div className="max-w-md">
                <h1 className="font-display text-5xl md:text-6xl tracking-tight leading-[0.95]">Sign in.</h1>
                <p className="mt-4 text-ink/70">
                  Restricted area. Enter your admin credentials to continue.
                </p>
                <form onSubmit={login} className="mt-10" data-testid="admin-login-form">
                  <div className="mb-6">
                    <label className="font-mono-tight text-[10px] text-ink/60">Email</label>
                    <input
                      type="email"
                      className="field"
                      data-testid="admin-email-input"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="admin@newgenpharma.ch"
                      autoComplete="username"
                      autoFocus
                      required
                    />
                  </div>
                  <div>
                    <label className="font-mono-tight text-[10px] text-ink/60">Password</label>
                    <input
                      type="password"
                      className="field"
                      data-testid="admin-password-input"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••••"
                      autoComplete="current-password"
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading || !email || !password}
                    data-testid="admin-login-button"
                    className="mt-10 px-8 py-4 bg-ink text-bone font-mono-tight text-[11px] inline-flex items-center gap-3 disabled:opacity-50"
                  >
                    <span className="w-1.5 h-1.5 bg-oxred inline-block" />
                    {loading ? "Verifying…" : "Sign in"}
                  </button>
                </form>
                <p className="mt-8 font-mono-tight text-[10px] text-ink/50">
                  Sessions expire after 12 hours. Contact the administrator to reset a lost password.
                </p>
              </div>
            </FadeUp>
          ) : (
            <div>
              <div className="flex items-baseline justify-between mb-8 border-b border-ink/15 pb-6 flex-wrap gap-4">
                <div className="flex items-baseline gap-10 flex-wrap">
                  <button
                    data-testid="admin-tab-inbox"
                    onClick={() => setTab("inbox")}
                    className={`font-display text-3xl md:text-4xl tracking-tight ${
                      tab === "inbox" ? "text-ink" : "text-ink/30 hover:text-ink/60"
                    }`}
                  >
                    Inbox <span className="font-mono-tight text-[11px] text-ink/50">·{inbox.length}</span>
                  </button>
                  <button
                    data-testid="admin-tab-nda"
                    onClick={() => setTab("nda")}
                    className={`font-display text-3xl md:text-4xl tracking-tight ${
                      tab === "nda" ? "text-ink" : "text-ink/30 hover:text-ink/60"
                    }`}
                  >
                    NDA Requests <span className="font-mono-tight text-[11px] text-ink/50">·{ndas.length}</span>
                  </button>
                  <button
                    data-testid="admin-tab-press"
                    onClick={() => setTab("press")}
                    className={`font-display text-3xl md:text-4xl tracking-tight ${
                      tab === "press" ? "text-ink" : "text-ink/30 hover:text-ink/60"
                    }`}
                  >
                    Press <span className="font-mono-tight text-[11px] text-ink/50">·{press?.total ?? 0}</span>
                  </button>
                  <button
                    data-testid="admin-tab-library"
                    onClick={() => setTab("library")}
                    className={`font-display text-3xl md:text-4xl tracking-tight ${
                      tab === "library" ? "text-ink" : "text-ink/30 hover:text-ink/60"
                    }`}
                  >
                    Library
                  </button>
                </div>
                <div className="flex items-center gap-6">
                  <span data-testid="admin-current-user" className="font-mono-tight text-[10px] text-ink/60">
                    {me.email}
                  </span>
                  <button
                    onClick={() => (tab === "inbox" ? refreshInbox() : tab === "nda" ? refreshNdas() : refreshPress())}
                    data-testid="admin-refresh"
                    className="font-mono-tight text-[11px] inline-flex items-center gap-2 link-underline"
                  >
                    <RefreshCw size={12} /> Refresh
                  </button>
                  <button
                    onClick={signOut}
                    data-testid="admin-signout"
                    className="font-mono-tight text-[11px] inline-flex items-center gap-2 link-underline"
                  >
                    <LogOut size={12} /> Sign out
                  </button>
                </div>
              </div>

              {tab === "library" ? (
                <AdminLibrary />
              ) : tab === "inbox" ? (
                inbox.length === 0 ? (
                  <div className="py-24 text-center font-mono-tight text-[11px] text-ink/60">No submissions yet.</div>
                ) : (
                  <div data-testid="admin-submissions-list" className="divide-y divide-ink/10 border-y border-ink/15">
                    {inbox.map((s) => (
                      <div key={s.id} data-testid={`admin-submission-${s.id}`} className="grid grid-cols-1 md:grid-cols-12 gap-4 py-8">
                        <div className="md:col-span-3">
                          <div className="font-display text-xl leading-tight">{s.name}</div>
                          <a href={`mailto:${s.email}`} className="font-mono-tight text-[11px] text-ink/70 link-underline">{s.email}</a>
                          {s.organization && <div className="mt-2 text-sm text-ink/70">{s.organization}</div>}
                        </div>
                        <div className="md:col-span-2">
                          <span className="font-mono-tight text-[10px] text-oxred">{s.interest}</span>
                          <div className="mt-2 font-mono-tight text-[10px] text-ink/50">{new Date(s.created_at).toLocaleString()}</div>
                        </div>
                        <div className="md:col-span-6 text-ink/85 leading-relaxed text-sm whitespace-pre-wrap">{s.message}</div>
                        <div className="md:col-span-1 md:text-right">
                          <button onClick={() => removeInbox(s.id)} data-testid={`admin-delete-${s.id}`} className="inline-flex items-center gap-2 font-mono-tight text-[10px] text-ink/60 hover:text-oxred">
                            <Trash2 size={12} /> Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              ) : tab === "press" ? (
                <div data-testid="admin-press-stats">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-ink/15 border border-ink/15">
                    <div className="bg-bone p-8">
                      <div className="font-mono-tight text-[10px] text-ink/60">Total downloads</div>
                      <div className="font-display text-5xl mt-3">{press?.total ?? 0}</div>
                    </div>
                    <div className="bg-bone p-8">
                      <div className="font-mono-tight text-[10px] text-ink/60">Last 14 days</div>
                      <div className="font-display text-5xl mt-3">{press?.last_14d ?? 0}</div>
                    </div>
                    {(press?.by_asset || []).slice(0, 2).map((b) => (
                      <div key={b.asset} className="bg-bone p-8">
                        <div className="font-mono-tight text-[10px] text-ink/60">{b.asset}</div>
                        <div className="font-display text-5xl mt-3">{b.count}</div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-12">
                    <div className="flex items-baseline justify-between mb-6 border-b border-ink/15 pb-4">
                      <h3 className="font-display text-2xl md:text-3xl tracking-tight">Per asset</h3>
                      <span className="font-mono-tight text-[11px] text-ink/60">All time</span>
                    </div>
                    <div className="divide-y divide-ink/10 border-y border-ink/15">
                      {(press?.by_asset || []).map((b) => (
                        <div key={b.asset} className="grid grid-cols-12 py-5 items-center">
                          <div className="col-span-4 font-mono text-sm">{b.asset}</div>
                          <div className="col-span-2 font-display text-3xl">{b.count}</div>
                          <div className="col-span-4 font-mono-tight text-[10px] text-ink/60">
                            {b.last ? `Last: ${new Date(b.last).toLocaleString()}` : "—"}
                          </div>
                          <div className="col-span-2">
                            <div className="h-[3px] bg-ink/10 relative w-full">
                              <div className="absolute top-0 left-0 h-[3px] bg-oxred" style={{ width: `${press?.total ? Math.min(100, Math.round((b.count / press.total) * 100)) : 0}%` }} />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="mt-12">
                    <div className="flex items-baseline justify-between mb-6 border-b border-ink/15 pb-4">
                      <h3 className="font-display text-2xl md:text-3xl tracking-tight">Recent activity</h3>
                      <span className="font-mono-tight text-[11px] text-ink/60">Last 25 downloads</span>
                    </div>
                    {(!press?.recent || press.recent.length === 0) ? (
                      <div className="py-16 text-center font-mono-tight text-[11px] text-ink/60">No downloads yet.</div>
                    ) : (
                      <div className="divide-y divide-ink/10 border-y border-ink/15">
                        {press.recent.map((r) => (
                          <div key={r.id} className="grid grid-cols-12 py-4 items-center gap-4">
                            <div className="col-span-3 font-mono text-sm">{r.asset}</div>
                            <div className="col-span-3 font-mono-tight text-[10px] text-ink/60">{new Date(r.created_at).toLocaleString()}</div>
                            <div className="col-span-6 font-mono-tight text-[10px] text-ink/50 truncate">{r.referer || "direct"} · {(r.user_agent || "").slice(0, 60)}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ) : ndas.length === 0 ? (
                <div className="py-24 text-center font-mono-tight text-[11px] text-ink/60">No NDA requests yet.</div>
              ) : (
                <div data-testid="admin-nda-list" className="divide-y divide-ink/10 border-y border-ink/15">
                  {ndas.map((n) => (
                    <div key={n.id} data-testid={`admin-nda-${n.id}`} className="grid grid-cols-1 md:grid-cols-12 gap-4 py-8">
                      <div className="md:col-span-3">
                        <div className="font-display text-xl leading-tight">{n.name}</div>
                        {n.title && <div className="font-mono-tight text-[10px] text-ink/60 mt-1">{n.title}</div>}
                        <a href={`mailto:${n.email}`} className="font-mono-tight text-[11px] text-ink/70 link-underline block mt-1">{n.email}</a>
                        <div className="mt-2 text-sm text-ink/85">{n.organization}</div>
                      </div>
                      <div className="md:col-span-2">
                        <span className={`font-mono-tight text-[10px] ${n.revoked ? "text-ink/40 line-through" : "text-oxred"}`}>{n.compound_id}</span>
                        {n.revoked && (
                          <span className="ml-2 font-mono-tight text-[10px] text-oxred">REVOKED</span>
                        )}
                        <div className="mt-2 font-mono-tight text-[10px] text-ink/50">{new Date(n.created_at).toLocaleString()}</div>
                        <div className="mt-1 font-mono-tight text-[10px] text-ink/50">Exp. in {daysLeft(n.expires_at)}d</div>
                        <div className="mt-1 font-mono-tight text-[10px] text-ink/50">
                          {n.downloads}/{n.max_downloads ?? 5} downloads
                        </div>
                        <div className="mt-1 font-mono-tight text-[10px] text-ink/50">
                          IP {n.ip_hash ? "pinned" : "not yet pinned"}
                        </div>
                      </div>
                      <div className="md:col-span-5 text-ink/85 leading-relaxed text-sm whitespace-pre-wrap">{n.intended_use}</div>
                      <div className="md:col-span-2 flex flex-col md:items-end gap-2">
                        <a href={`${BACKEND_URL}/api/nda/nda/${n.token}`} target="_blank" rel="noreferrer" data-testid={`admin-nda-download-nda-${n.id}`} className="inline-flex items-center gap-2 font-mono-tight text-[10px] link-underline">
                          <FileDown size={12} /> NDA PDF
                        </a>
                        <a href={`${BACKEND_URL}/api/nda/dossier/${n.token}`} target="_blank" rel="noreferrer" data-testid={`admin-nda-download-dossier-${n.id}`} className="inline-flex items-center gap-2 font-mono-tight text-[10px] link-underline">
                          <FileDown size={12} /> Dossier PDF
                        </a>
                        <button
                          onClick={() => toggleRevoke(n)}
                          data-testid={`admin-nda-revoke-${n.id}`}
                          className={`inline-flex items-center gap-2 font-mono-tight text-[10px] ${
                            n.revoked ? "text-ink/60 hover:text-ink" : "text-oxred hover:text-oxred/80"
                          }`}
                        >
                          {n.revoked ? (<><RotateCcw size={12} /> Re-enable</>) : (<><Ban size={12} /> Revoke</>)}
                        </button>
                        <button onClick={() => removeNda(n.id)} data-testid={`admin-nda-delete-${n.id}`} className="mt-2 inline-flex items-center gap-2 font-mono-tight text-[10px] text-ink/60 hover:text-oxred">
                          <Trash2 size={12} /> Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </PageShell>
  );
}
